import {useState} from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {getUserByToken, login} from '../core/_requests'
import {useAuth} from '../core/Auth'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {decryptId} from '_metronic/helpers/Encrypt'

const initialValues = {
  username: localStorage.getItem('username') || '',
  password: localStorage.getItem('password') ? decryptId(localStorage.getItem('password')) : '',
  rememberMe: false,
}

export function Login() {
  const {t} = useTranslation()

  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const {saveAuth, setCurrentUser} = useAuth()

  const loginSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(100, t('VALIDATION.MAX_CHARACTER_', {quantity: 100}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.USERNAME')})),

    password: Yup.string()
      .min(3, t('VALIDATION.MIN_CHARACTER_', {quantity: 3}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.PASSWORD')})),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: loginSchema,

    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)

      try {
        const {data: auth} = await login(values.username, values.password)

        saveAuth(auth)

        const {data: user} = await getUserByToken(auth.api_token)

        setCurrentUser(user)
      } catch (error: any) {
        if (error.response?.data) {
          if (error.response.status === 404) {
            setStatus(t('MESSAGES.NOT_FOUND_ERROR'))
          } else {
            setStatus(error.response.data.message)
          }
        } else {
          console.log('Error', error.message)
        }

        saveAuth(undefined)
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
      <div className='text-center mb-5'>
        <h2 className='fw-bold mb-1'>{t('GLOBAL.LOGIN_PAGE')}</h2>
      </div>

      {formik.status && (
        <div className='alert alert-danger mb-4'>
          <div className='alert-text fw-bold'>{formik.status}</div>
        </div>
      )}

      {/* Username */}
      <div className='fv-row mb-4'>
        <label className='form-label fw-bold text-primary'>{t('GLOBAL.USERNAME')}</label>

        <input
          type='text'
          placeholder={t('GLOBAL.ENTER_USERNAME')}
          {...formik.getFieldProps('username')}
          className={clsx(
            'form-control form-control-lg',
            formik.touched.username && formik.errors.username && 'is-invalid',
            formik.touched.username && !formik.errors.username && 'is-valid'
          )}
        />

        {formik.touched.username && formik.errors.username && (
          <div className='text-danger mt-1 fw-bold fs-7'>{formik.errors.username}</div>
        )}
      </div>

      {/* Password */}
      <div className='fv-row mb-4'>
        <label className='form-label fw-bold text-primary'>{t('GLOBAL.PASSWORD')}</label>

        <div className='position-relative'>
          <input
            type={showPassword ? 'text' : 'password'}
            placeholder={t('GLOBAL.ENTER_PASSWORD')}
            {...formik.getFieldProps('password')}
            className={clsx(
              'form-control form-control-lg',
              formik.touched.password && formik.errors.password && 'is-invalid',
              formik.touched.password && !formik.errors.password && 'is-valid'
            )}
            style={{paddingRight: '5rem'}}
          />

          <button
            type='button'
            onClick={() => setShowPassword((prev) => !prev)}
            className='btn btn-sm btn-icon position-absolute top-50 translate-middle-y'
            style={{
              right: '2.5rem',
              zIndex: 5,
            }}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <i
              className={
                showPassword
                  ? 'fas fa-eye-slash fs-5 text-gray-600'
                  : 'fas fa-eye fs-5 text-gray-600'
              }
            ></i>
          </button>
        </div>

        {formik.touched.password && formik.errors.password && (
          <div className='text-danger mt-1 fw-bold fs-7'>{formik.errors.password}</div>
        )}
      </div>

      <div className='mb-5'>
        <div className='d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-3 mb-4'>
          <label
            className='form-check d-flex align-items-center gap-2 m-0 cursor-pointer'
            htmlFor='rememberMe'
          >
            <input
              id='rememberMe'
              type='checkbox'
              className='form-check-input m-0'
              name='rememberMe'
              checked={formik.values.rememberMe}
              onChange={formik.handleChange}
            />

            <span className='fw-semibold text-gray-700'>{t('GLOBAL.REMEMBER_ME')}</span>
          </label>

          <Link to='/forgot-password' className='fw-semibold text-primary text-decoration-none'>
            {t('GLOBAL.FORGOT_PASSWORD')}
          </Link>
        </div>

        <button
          type='submit'
          className='btn btn-primary w-100 py-3 fs-5 fw-bold rounded'
          disabled={formik.isSubmitting || !formik.isValid}
        >
          {!loading && <span>{t('GLOBAL.LOGIN')}</span>}

          {loading && <span className='spinner-border spinner-border-sm ms-2'></span>}
        </button>

        <div className='d-flex align-items-center gap-3 my-4'>
          <div className='flex-grow-1 border-top'></div>
          <span className='text-muted small fw-semibold'>OR</span>
          <div className='flex-grow-1 border-top'></div>
        </div>

        <Link
          to='/register'
          className='btn btn-light-primary w-100 py-3 rounded-3 fw-bold d-flex align-items-center justify-content-center gap-2'
        >
          <span className='fas fa-user-plus'></span>

          <span>{t('GLOBAL.BECOME_MEMBER')}</span>
        </Link>

        <div className='text-center mt-2'>
          <span className='text-muted small'>{t('GLOBAL.JOIN_US')}</span>
        </div>
      </div>
    </form>
  )
}
